import { useAppStore, type TextScale } from '../store/app-store.js';
import { fontFamily } from './font-family.js';
import { fontWeight } from './font-weight.js';
import { letterSpacing } from './letter-spacing.js';

export const TEXT_SCALE_FACTORS: Record<TextScale, number> = {
  xs: 0.875,
  sm: 0.9375,
  md: 1,
  lg: 1.125,
  xl: 1.25,
  xxl: 1.5
};

export interface TypographyBundle {
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  lineHeight: number;
  letterSpacing: string;
  textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
}

export type TypographyRole =
  | 'display'
  | 'title'
  | 'headline'
  | 'subtitle'
  | 'bodyLarge'
  | 'body'
  | 'bodySmall'
  | 'caption'
  | 'label'
  | 'overline'
  | 'code'
  | 'codeBlock'
  | 'button'
  | 'inputLabel'
  | 'helperText'
  | 'tableHeader';

export const typographyRoles: Record<TypographyRole, TypographyBundle> = {
  display:     { fontFamily: fontFamily.system, fontSize: 60, fontWeight: fontWeight.extrabold, lineHeight: 1.0,  letterSpacing: letterSpacing.tighter },
  title:       { fontFamily: fontFamily.system, fontSize: 36, fontWeight: fontWeight.bold,      lineHeight: 1.15, letterSpacing: letterSpacing.tight },
  headline:    { fontFamily: fontFamily.system, fontSize: 24, fontWeight: fontWeight.bold,      lineHeight: 1.25, letterSpacing: letterSpacing.tight },
  subtitle:    { fontFamily: fontFamily.system, fontSize: 18, fontWeight: fontWeight.semibold,  lineHeight: 1.4,  letterSpacing: letterSpacing.normal },
  bodyLarge:   { fontFamily: fontFamily.system, fontSize: 18, fontWeight: fontWeight.normal,    lineHeight: 1.6,  letterSpacing: letterSpacing.normal },
  body:        { fontFamily: fontFamily.system, fontSize: 16, fontWeight: fontWeight.normal,    lineHeight: 1.5,  letterSpacing: letterSpacing.normal },
  bodySmall:   { fontFamily: fontFamily.system, fontSize: 14, fontWeight: fontWeight.normal,    lineHeight: 1.5,  letterSpacing: letterSpacing.normal },
  caption:     { fontFamily: fontFamily.system, fontSize: 12, fontWeight: fontWeight.normal,    lineHeight: 1.4,  letterSpacing: letterSpacing.normal },
  label:       { fontFamily: fontFamily.system, fontSize: 14, fontWeight: fontWeight.medium,    lineHeight: 1.4,  letterSpacing: letterSpacing.normal },
  overline:    { fontFamily: fontFamily.system, fontSize: 11, fontWeight: fontWeight.semibold,  lineHeight: 1.5,  letterSpacing: letterSpacing.widest, textTransform: 'uppercase' },
  code:        { fontFamily: fontFamily.mono,   fontSize: 14, fontWeight: fontWeight.normal,    lineHeight: 1.5,  letterSpacing: letterSpacing.normal },
  codeBlock:   { fontFamily: fontFamily.mono,   fontSize: 13, fontWeight: fontWeight.normal,    lineHeight: 1.6,  letterSpacing: letterSpacing.normal },
  button:      { fontFamily: fontFamily.system, fontSize: 14, fontWeight: fontWeight.semibold,  lineHeight: 1.0,  letterSpacing: letterSpacing.wide },
  inputLabel:  { fontFamily: fontFamily.system, fontSize: 13, fontWeight: fontWeight.medium,    lineHeight: 1.4,  letterSpacing: letterSpacing.normal },
  helperText:  { fontFamily: fontFamily.system, fontSize: 12, fontWeight: fontWeight.normal,    lineHeight: 1.4,  letterSpacing: letterSpacing.normal },
  tableHeader: { fontFamily: fontFamily.system, fontSize: 12, fontWeight: fontWeight.semibold,  lineHeight: 1.4,  letterSpacing: letterSpacing.wide, textTransform: 'uppercase' }
};

export const typeRoleKeys: TypographyRole[] = Object.keys(typographyRoles) as TypographyRole[];

function getStoreTextScale(): TextScale {
  try {
    return useAppStore.getState().textScale;
  } catch {
    return 'md';
  }
}

export interface TypographyOptions {
  scale?: TextScale;
}

export function typography(role: TypographyRole, opts: TypographyOptions = {}): TypographyBundle {
  const base = typographyRoles[role];
  const scale = opts.scale ?? getStoreTextScale();
  const factor = TEXT_SCALE_FACTORS[scale];
  return {
    ...base,
    fontSize: Math.round(base.fontSize * factor),
    lineHeight: Math.round(base.fontSize * base.lineHeight * factor) / base.fontSize
  };
}
